export type SanitizeFileNameOptions = {
  /** Replacement for each run of illegal/control characters. Default: "-" */
  replacement?: string;
  /** Returned when the sanitized result would otherwise be empty. Default: "file" */
  fallback?: string;
};

const ILLEGAL_FILENAME_CHARS = /[<>:"/\\|?*\x00-\x1F]+/g;

// Invisible characters that carry no content of their own -- removed entirely rather than
// replaced, since collapsing them to a visible replacement char would misleadingly split what's
// otherwise one continuous word. Covers soft hyphen (U+00AD), the zero-width
// space/joiner/non-joiner/word-joiner/LTR-mark/RTL-mark family (U+200B-U+200F), word joiner
// (U+2060), and the bidi control characters -- embeddings/overrides (U+202A-U+202E) and isolates
// (U+2066-U+2069). The bidi controls matter for filenames specifically: they're the mechanism
// behind "right-to-left override" filename-spoofing tricks (e.g. disguising a .exe as a .txt by
// reversing how the extension renders). Deliberately excludes U+FEFF (BOM) -- JS's \s already
// matches it, so it's treated as whitespace (collapsed to a space) rather than removed outright,
// which reads better when it shows up between two words from a pasted document.
const INVISIBLE_CHARS = /[\u00AD\u200B-\u200F\u2060\u202A-\u202E\u2066-\u2069]/g;

/**
 * Strips characters illegal in Windows/Mac/Linux filenames (plus control and invisible/bidi
 * characters) and collapses whitespace, producing a string safe to use as a downloaded file's
 * name.
 */
export function sanitizeFileName(value: string, options?: SanitizeFileNameOptions): string {
  const replacement = options?.replacement ?? "-";
  const fallback = options?.fallback ?? "file";

  const sanitized = value
    .replace(INVISIBLE_CHARS, "")
    .replace(ILLEGAL_FILENAME_CHARS, replacement)
    .replace(/\s+/g, " ")
    .trim();

  return sanitized || fallback;
}
